import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Tooltip,
  useDisclosure,
  useToast,
  UseToastOptions,
} from '@chakra-ui/react';
import { DeleteOrderedProjectModal } from '~/subdomains/OrdersOverview/Orders/common/DeleteOrderedProjectModal';
import { OrderProjectHistoryModal } from '~/subdomains/OrdersOverview/Orders/common/OrderProjectHistoryModal';
import { EditOrderedProjectModal } from '~/subdomains/OrdersOverview/Orders/common/EditOrderedProjectModal';
import {
  useGetSinglePriolistPdf,
  useGetFullPriolistPdf,
} from '~/common/api/pdfServiceApi/PdfApi';
import { TotalCalculatedTimes } from '~/subdomains/OrdersOverview/Orders/common/TotalCalculatedTimes';
import {
  AddIcon,
  BellIcon,
  DeleteIcon,
  EditIcon,
  SearchIcon,
} from '@chakra-ui/icons';
import { CollapseButton } from '~/common/components/defaultUIComponents/CollapseButton';
import { useOrderedProjects } from '~/common/api/orderingServiceApi/OrderingApi';
import { Heading } from '~/common/components/defaultUIComponents/Heading';
import { DataTable } from '~/subdomains/OrdersOverview/Orders/DataTable';
import { useImportant } from '~/subdomains/OrdersOverview/Orders/utils';
import { Link } from '~/common/components/defaultUIComponents/Link';
import { OrderedProject, ViewState } from '@its4plan/ordering';
import { ListView } from '~/common/components/ListView';
import routes from '~/common/constants/routes.json';
import useTranslate from '~/common/utils/translate';
import { CloudDownload } from '@material-ui/icons';
import { useMemo, useState } from 'react';
import _ from 'lodash';

export function PriolistTab() {
  const t = useTranslate();
  const toast = useToast();
  const { refetch: fetchPdf, isFetching: isPdfLoading } =
    useGetFullPriolistPdf();
  const handlePdfDownload = async (): Promise<void> => {
    const showToast = (
      title: string,
      description: string,
      status: UseToastOptions['status'],
      duration: number,
    ): void => {
      toast({
        title,
        description,
        status,
        duration,
        position: 'top',
        variant: 'solid',
      });
    };
    try {
      const { data } = await fetchPdf();
      if (data) {
        const blob = new Blob([data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = t('weekplan.pdf.fullpdf.filename')!;
        a.click();
        window.URL.revokeObjectURL(url);
        showToast(
          t('weekplan.pdf.fullpdf.successToast.title')!,
          t('weekplan.pdf.fullpdf.successToast')!,
          'success',
          3000,
        );
      }
    } catch (error) {
      showToast(
        t('weekplan.pdf.fullpdf.errorToast.title')!,
        t('weekplan.pdf.fullpdf.errorToast')!,
        'error',
        5000,
      );
    }
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [viewState, setViewState] = useState<ViewState | null>(null);

  const { data: orderedProjects = [], isLoading } = useOrderedProjects({
    request: { viewState: viewState ?? undefined },
    select: (data) =>
      _.groupBy(data, (orderedProject) => orderedProject.priority) as {
        [key: number]: OrderedProject[];
      },
  });

  const { uncheckedDone, uncheckedPartiallyDone } = useImportant();

  const changeViewState = (newViewState: ViewState | null): void => {
    if (newViewState !== viewState) {
      setSearchTerm('');
    }
    setViewState(newViewState);
  };

  return (
    <div>
      <HStack pb={3}>
        <Link href={routes.CREATE_ORDER_V2}>
          <IconButton isRound aria-label="add-orders" icon={<AddIcon />} />
        </Link>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon />
          </InputLeftElement>
          <Input
            type="search"
            placeholder="Arbeitsplanungseinträge suchen"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.currentTarget.value)}
          />
        </InputGroup>
        <HStack>
          <Button
            onClick={() => changeViewState(null)}
            bg={viewState === null ? 'green' : 'white'}
            color={viewState === null ? 'white' : 'black'}
            borderColor="gray.500"
            borderWidth="1px"
            variant={viewState === null ? 'solid' : 'outline'}
          >
            {t('ViewState.ALL')}
          </Button>
          <Button
            onClick={() => changeViewState(ViewState.PARTIALLY_DONE)}
            bg={viewState === ViewState.PARTIALLY_DONE ? 'green' : 'white'}
            color={
              uncheckedPartiallyDone > 0
                ? 'red.500'
                : viewState === ViewState.PARTIALLY_DONE
                  ? 'white'
                  : 'black'
            }
            borderColor="gray.500"
            borderWidth="1px"
            variant={
              viewState === ViewState.PARTIALLY_DONE ? 'solid' : 'outline'
            }
          >
            {t(`ViewState.${ViewState.PARTIALLY_DONE}`)}
            {uncheckedPartiallyDone > 0 ? ` (${uncheckedPartiallyDone})` : ''}
          </Button>
          <Button
            onClick={() => changeViewState(ViewState.DONE)}
            bg={viewState === ViewState.DONE ? 'green' : 'white'}
            color={
              uncheckedDone > 0
                ? 'red.500'
                : viewState === ViewState.DONE
                  ? 'white'
                  : 'black'
            }
            borderColor="gray.500"
            borderWidth="1px"
            variant={viewState === ViewState.DONE ? 'solid' : 'outline'}
          >
            {t(`ViewState.${ViewState.DONE}`)}
            {uncheckedDone > 0 ? ` (${uncheckedDone})` : ''}
          </Button>
        </HStack>
        <Tooltip label={t('weekplan.button.showPDFOfAllWeeks.text') as string}>
          <IconButton
            aria-label="pdf-export"
            icon={<CloudDownload />}
            onClick={() => {
              void handlePdfDownload();
            }}
            isLoading={isPdfLoading}
          />
        </Tooltip>
      </HStack>
      <TotalCalculatedTimes />
      <ListView
        data={Object.keys(orderedProjects).map(Number)}
        isLoading={isLoading}
        search={''}
        searchFields={() => ['']}
        orderBy={(d) => d}
        render={({ data }) => (
          <Stack gap={3}>
            {data.map((priority) => (
              <PriorityItem
                priority={priority}
                key={`prio-item-${priority}`}
                projects={orderedProjects[priority]!}
                searchTerm={searchTerm}
              />
            ))}
          </Stack>
        )}
      />
    </div>
  );
}

function PriorityItem(props: {
  priority: number;
  projects: OrderedProject[];
  searchTerm: string;
}) {
  const historyWasUpdated = useMemo(() => {
    return props.projects.some(
      (orderedProject) =>
        !orderedProject.checkedIn && orderedProject.doneAmount > 0,
    );
  }, [props.projects]);
  const t = useTranslate();
  const toast = useToast();
  const { refetch: fetchPdf, isFetching: isPdfLoading } =
    useGetSinglePriolistPdf({
      request: { prio: props.priority },
      enabled: false,
    });
  const handlePdfDownload = async (): Promise<void> => {
    const showToast = (
      title: string,
      description: string,
      status: UseToastOptions['status'],
      duration: number,
    ): void => {
      toast({
        title,
        description,
        status,
        duration,
        position: 'top',
        variant: 'solid',
      });
    };
    try {
      const { data } = await fetchPdf();
      if (data) {
        const blob = new Blob([data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = t('weekplan.pdf.singlepdf.filename')!.replace(
          /\.pdf$/,
          `_${props.priority}.pdf`,
        );
        a.click();
        window.URL.revokeObjectURL(url);
        showToast(
          t('weekplan.pdf.fullpdf.successToast.title')!,
          t('weekplan.pdf.fullpdf.successToast')!,
          'success',
          3000,
        );
      }
    } catch (error) {
      showToast(
        t('weekplan.pdf.fullpdf.errorToast.title')!,
        t('weekplan.pdf.fullpdf.errorToast')!,
        'error',
        5000,
      );
    }
  };
  const { isOpen, onToggle } = useDisclosure();
  return (
    <Card>
      <CardHeader
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Heading size="lg">Prio {props.priority}</Heading>
        <Flex alignItems="center" gap={2}>
          {historyWasUpdated && (
            <BellIcon ml={2} h={10} w={10} color="red.600" />
          )}
          <Tooltip label={t('weekplan.button.showPDF.text') as string}>
            <IconButton
              aria-label="pdf-export"
              isRound
              size="sm"
              icon={<CloudDownload />}
              onClick={() => {
                void handlePdfDownload();
              }}
              isLoading={isPdfLoading}
            />
          </Tooltip>
          <CollapseButton isOpen={isOpen} toggleIsOpen={onToggle} />
        </Flex>
      </CardHeader>
      {isOpen && (
        <ListView
          data={props.projects}
          search={props.searchTerm}
          searchFields={(orderedProject) => [
            orderedProject.project.name,
            orderedProject.orderInformation.customerName,
          ]}
          orderBy={(orderedProject) => orderedProject.project.name}
          render={({ data }) => (
            <CardBody>
              <DataTable
                data={data}
                columns={[
                  {
                    name: t('priolist.table.customerName') as string,
                    get: (row) => row.orderInformation.customerName,
                    width: '100px',
                  },
                  {
                    name: t('priolist.table.project') as string,
                    render: (props) => (
                      <Link
                        href={routes.PROJECT.replace(
                          ':id',
                          props.row.project.foreignId.toString(),
                        )}
                      >
                        {props.row.project.name}
                      </Link>
                    ),
                    width: '100px',
                  },
                  {
                    name: t('priolist.table.options') as string,
                    get: (row) =>
                      row.options
                        .map((option) => option.project.name)
                        .join(', '),
                    width: '150px',
                  },
                  {
                    name: t('priolist.table.amount') as string,
                    get: (row) => row.amount.toString(),
                    isNumeric: true,
                    width: '70px',
                  },
                  {
                    name: t('priolist.table.doneAmount') as string,
                    get: (row) => row.doneAmount.toString(),
                    isNumeric: true,
                    width: '90px',
                  },
                  {
                    name: t('ordertile.comment') as string,
                    get: (row) => row.comment ?? '',
                    width: '500px',
                  },
                  {
                    name: t('priolist.table.actions') as string,
                    actions: (props) => (
                      <Flex
                        justifyContent="flex-start"
                        style={{
                          whiteSpace: 'nowrap',
                          marginLeft: '0',
                        }}
                      >
                        <PriolistActions {...props} />
                      </Flex>
                    ),
                    width: '200px',
                  },
                ]}
              />
            </CardBody>
          )}
        />
      )}
    </Card>
  );
}

function PriolistActions(props: { row: OrderedProject; index: number }) {
  const { row: orderedProject } = props;
  const t = useTranslate();
  const { isOpen: isDeleteOpen, onToggle: toggleDeleteOpen } = useDisclosure();
  const { isOpen: historyOpen, onToggle: toggleHistoryOpen } = useDisclosure();
  const { isOpen: isEditOpen, onToggle: toggleEditOpen } = useDisclosure();

  const historyWasUpdated = useMemo(() => {
    return !orderedProject.checkedIn && orderedProject.doneAmount > 0;
  }, [orderedProject]);

  const finished = useMemo(() => {
    return orderedProject.doneAmount >= orderedProject.amount;
  }, [orderedProject]);

  return (
    <Flex justifyContent="flex-end" alignItems="center" gap={1}>
      <DeleteOrderedProjectModal
        orderedProjectId={orderedProject.id}
        open={isDeleteOpen}
        toggle={toggleDeleteOpen}
      />
      <Tooltip
        label={t('ordertile.button.editOrder.text')}
        aria-label={t('ordertile.button.editOrder.text')}
      >
        <IconButton
          icon={<EditIcon />}
          onClick={() => toggleEditOpen()}
          aria-label={t('ordertile.button.editOrder.text') as string}
          isRound
          size="sm"
        />
      </Tooltip>
      <EditOrderedProjectModal
        orderedProject={orderedProject}
        isOpen={isEditOpen}
        close={toggleEditOpen}
      />
      <Tooltip
        label={t('ordertile.button.delete.text')}
        aria-label={t('ordertile.button.delete.text') as string}
      >
        <IconButton
          icon={<DeleteIcon />}
          onClick={toggleDeleteOpen}
          aria-label={t('ordertile.button.delete.text') as string}
          isRound
          size="sm"
        />
      </Tooltip>
      <Button
        onClick={toggleHistoryOpen}
        color={historyWasUpdated ? 'red.600' : 'white'}
        size="sm"
      >
        {t('ordertile.orderProject.doneAmount.text')}{' '}
        {orderedProject.doneAmount}
        {historyWasUpdated && <BellIcon ml={2} color="red.600" />}
      </Button>
      <OrderProjectHistoryModal
        orderedProjectId={orderedProject.id}
        history={orderedProject.history}
        checkedIn={orderedProject.checkedIn}
        isOpen={historyOpen}
        onToggle={toggleHistoryOpen}
        finish={finished}
      />
    </Flex>
  );
}
